"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useId } from "react";
import { Controller, useForm } from "react-hook-form";
import { Box, Button, FormControl, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup } from "@/components/controls/base-controls";
import { type MfaFormData, mfaFormSchema } from "@/core/schemas/mfa.schema";
// import { setMFAPreference } from "@/core/services/user.service";
import { MFA, type MfaType } from "@/core/types/mfa.type";
import { useLoadingStore } from "@/core/utils/loading.store";

type MfaPreferenceFormProps = {
    preferredMfa: MfaType | undefined,
    onMfaSubmit: (data: MfaType) => void
};

const MfaPreferenceForm = ({ preferredMfa, onMfaSubmit }: MfaPreferenceFormProps) => {
    const t = useTranslations();

    const { isLoading } = useLoadingStore();

    const preferred_id = useId();

    const { 
        control, 
        handleSubmit, 
        formState: { errors } 
    } = useForm<MfaFormData>({
        mode: "onSubmit",
        resolver: zodResolver(mfaFormSchema),
        defaultValues: { 
            preferred: preferredMfa
         },
    });

    const onSubmit = (data: MfaFormData) => {
        onMfaSubmit(data.preferred);
    };

    return (
        <Box
            component="form"
            noValidate
            aria-busy={isLoading} aria-describedby="data-processing"
            style={{ display: "flex", flexDirection: "column", flex: 1 }}
            onSubmit={handleSubmit(onSubmit)}
        >
            <FormControl error={!!errors.preferred}>
                <FormLabel id="hook-form-radio-label">{t("mfa.preference")}</FormLabel>
                <Controller
                    name="preferred"
                    control={control}
                    render={({ field }) => (
                        <RadioGroup 
                            {...field} 
                            sx={{ width: "100%" }}
                            id={preferred_id}
                            aria-labelledby="hook-form-radio-label"
                        >
                            <FormControlLabel value={MFA.TOTP} control={<Radio />} label={t(`mfa.${MFA.TOTP}`)} />
                            {/* <FormControlLabel value={MFA.SMS} control={<Radio />} label={t(`mfa.${MFA.SMS}`)} /> */}
                            <FormControlLabel value={MFA.EMAIL} control={<Radio />} label={t(`mfa.${MFA.EMAIL}`)} />
                        </RadioGroup>
                    )}
                />

                {errors.preferred && (
                    <FormHelperText>{errors.preferred.message}</FormHelperText>
                )}
            </FormControl>

            <Button
                variant="contained"
                color="secondary"
                sx={{
                    borderRadius: '5px',
                    padding: "1.2em 2.2em",
                    width: "max-content",
                    minWidth: 0,
                    flexShrink: 1,
                    textTransform: "none"
                }}
                type="submit"
            >
                {t("mfa.submit")}
            </Button>

        </Box>
    )
};

export default MfaPreferenceForm;
